import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { auth } from './firebase';
import Header from './components/Header';
import Hero from './sections/Hero';
import ExamInfo from './sections/ExamInfo';
import About from './sections/About';
import Features from './sections/Features';
import HowItWorks from './sections/HowItWorks';
import Testimonials from './sections/Testimonials';
import SubscriptionPlans from './sections/SubscriptionPlans';
import Screenshots from './sections/Screenshots';
import Availability from './sections/Availability';
import Contact from './sections/Contact';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PoliticaPrivacidad from './pages/PoliticaPrivacidad';
import AnniversaryPage from './pages/AnniversaryPage';
import CursosVortexPage from './pages/CursosVortexPage';
import GuiasPage from './pages/GuiasPage';
import LectorTemasPreview from './pages/LectorTemasPreview';
import AnniversaryOverlay from './components/AnniversaryOverlay';
import CursosCarousel from './sections/CursosCarousel';
import { setAdminStatus } from './utils/logger';
import ToastContainer from './components/ToastContainer';
import UniversityCareerSelection from './components/UniversityCareerSelection';
import ConvocatoriasUniversidadesSection from './sections/ConvocatoriasUniversidadesSection';
import UniversidadesConvocatoriasPage from './pages/UniversidadesConvocatoriasPage';
import CalendarioExamenes from './components/CalendarioExamenes';
import './styles/UniversityCareerSelection.css';
import './styles/ConvocatoriasUniversidades.css';
import './styles/UniversidadesConvocatorias.css';

// Componente para la página principal
const LandingPage = () => (
    <>
      <AnniversaryOverlay />
      <Header />
      <Hero />
      <ExamInfo />
      <CalendarioExamenes />
      <CursosCarousel />
      <About />
      <Features />
      <HowItWorks />
      <Testimonials />
      <SubscriptionPlans />
      <Screenshots />
      <Availability />
      <Contact />
      <Footer />
      <ChatWidget />
    </>
  );

// Redirección legada /cursos-vortex → /cursos preservando query params (p.ej. ?courseId=xxx)
const CursosVortexRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/cursos', search: location.search }} replace />;
};

import AccessDenied from './components/AccessDenied';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentPending from './components/PaymentPending';
import PaymentFailure from './components/PaymentFailure';
import PurchaseLicense from './components/PurchaseLicense';


import StripeSuccess from './components/StripeSuccess';
import StripeCancel from './components/StripeCancel';
import { useAuth } from './hooks/useAuth';
import { useAdmin } from './hooks/useAdmin';
import { useUserPresence } from './hooks/useUserPresence';
import { ShadowModeProvider } from './contexts/ShadowModeContext';
import ShadowBanner from './components/ShadowBanner';
import LoadingSpinner from './components/LoadingSpinner';

// Componente para proteger rutas premium y verificar configuración universidad/carrera
const PremiumProtectedRoute = ({ children }) => {
  const { user, loading, authLoading, isPremium, profileData, profileLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin(user);
  const [showSelection, setShowSelection] = useState(false);

  useEffect(() => {
    if (!loading && !adminLoading && !profileLoading && user && isPremium && !isAdmin) {
      // Verificar si ya tiene universidad y carrera configurada
      const hasConfig = profileData?.universidadId && profileData?.carreraId;
      if (!hasConfig) {
        setShowSelection(true);
      }
    }
  }, [loading, adminLoading, profileLoading, user, isPremium, isAdmin, profileData]);

  if (authLoading || adminLoading || (!isAdmin && (loading || profileLoading))) {
    return (
      <LoadingSpinner
        label="Verificando licencia"
        height="100vh"
        style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
      />
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isPremium && !isAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  if (showSelection) {
    return (
      <UniversityCareerSelection 
        user={user} 
        onComplete={(data) => {
          if (data) {
            setShowSelection(false);
          }
        }} 
      />
    );
  }

  return children;
};

function AppContent() {
  const { user, authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin(user);
  useUserPresence();

  // Sincronizar estado de admin con el logger global.
  // Cuando isAdmin cambia, se activa o silencia la consola en producción.
  useEffect(() => {
    setAdminStatus(isAdmin);
  }, [isAdmin]);

  useEffect(() => {
    const blockPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        alert('Impresión deshabilitada en esta página.');
      }
    };
    window.addEventListener('keydown', blockPrint);
    return () => window.removeEventListener('keydown', blockPrint);
  }, []);

  if (authLoading || adminLoading) {
    return (
      <LoadingSpinner
        label="Cargando"
        height="100vh"
        style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
      />
    );
  }

  return (
    <>
      <ShadowBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/cursos" element={<CursosVortexPage />} />
        <Route path="/guias" element={<GuiasPage />} />
        <Route path="/cursos-vortex" element={<CursosVortexRedirect />} />
        <Route path="/lector-preview" element={<LectorTemasPreview />} />
        <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard/*"
        element={
          <PremiumProtectedRoute>
            <Dashboard />
          </PremiumProtectedRoute>
        }
      />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/pending" element={<PaymentPending />} />
      <Route path="/payment/failure" element={<PaymentFailure />} />
      <Route path="/purchase-license" element={<PurchaseLicense />} />
      <Route path="/stripe-checkout" element={<Navigate to="/purchase-license" replace />} />
      <Route path="/stripe/success" element={<StripeSuccess />} />
      <Route path="/stripe/cancel" element={<StripeCancel />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
      <Route path="/aniversario" element={<AnniversaryPage />} />
      <Route path="/universidades-convocatorias" element={<UniversidadesConvocatoriasPage />} />
      <Route path="/universidades-convocatorias/:id" element={<UniversidadesConvocatoriasPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <ShadowModeProvider>
        <AppContent />
        <ToastContainer />
      </ShadowModeProvider>
    </Router>
  )
}

export default App